import {
  RECOGNITION_AWARD_TYPES,
  RECOGNITION_REDEMPTION_METHOD_CHOICES,
  RECOGNITION_MILESTONE_EVENT_OPTIONS,
} from '@/pages/recognition/constants';

const { PROGRAM, OPEN, MILESTONE } = RECOGNITION_AWARD_TYPES;
const ALL = [PROGRAM, OPEN, MILESTONE];
const PROGRAM_OPEN = [PROGRAM, OPEN];
// All award types have banner/badge imagery; only program/milestone have the
// "enable badges" (collectible home-page badge) toggle — open awards always show one.
const WITH_IMAGES = ALL;
const WITH_BADGE_TOGGLE = [PROGRAM, MILESTONE];
// Open awards are non-monetary peer-to-peer shout-outs, so every money-bearing field
// (amounts, budget, spend categories, redemption, expiry, approvers) excludes them.
const WITH_MONETARY = [PROGRAM, MILESTONE];

const AMOUNT_MODE_OPTIONS = [
  { text: 'Fixed', value: 'fixed' },
  { text: 'Adjustable', value: 'free' },
];

// Gating helpers — a field is disabled (renders "—") when its conditional returns false.
const monetaryOn = row => Boolean(row.monetary_enabled);
const monetaryFixed = row => Boolean(row.monetary_enabled) && row.amount_mode !== 'free';
const monetaryFree = row => Boolean(row.monetary_enabled) && row.amount_mode === 'free';

/**
 * Declarative description of every editable award field, mirroring the create-award form.
 *
 * The tabular editor renders one column per field (filtered to the group's award type).
 *
 * - type: text | number | currency | budget | duration-months | select | multiselect | toggle
 *         | employee-multiselect | division-multiselect | image | modal
 * - modal: for type 'modal' — which editor modal opens ('amounts' | 'categories' | 'company-values')
 * - source: 'column' (top-level program column) | 'properties' (nested in properties jsonb)
 *           | 'virtual' (no direct backend key; the row model derives/serialises it)
 * - appliesTo: award types the field is relevant for (this is how per-type differences are expressed)
 * - conditional: optional (row) => boolean; when false the cell renders disabled ("—")
 */
// Optional-feature toggles. These live in the editor's leading "Optional toggles"
// column (not as their own data columns). Each `key` is a per-row boolean; `toggle`
// on a field below links that column to its controlling switch.
export const AWARD_FEATURE_TOGGLES = [
  { key: 'company_values_enabled', label: 'Company values', appliesTo: PROGRAM_OPEN },
  { key: 'show_badge', label: 'Enable badges', appliesTo: WITH_BADGE_TOGGLE },
  {
    key: 'allow_nominator_image_choice',
    label: 'User selects banner',
    appliesTo: [OPEN],
    // Mirrors the single-award form: choosing which banner is shown needs 2+ images.
    requiresMultipleBannerImages: true,
  },
  { key: 'monetary_enabled', label: 'Monetary reward', appliesTo: WITH_MONETARY },
  // Spend categories only apply to monetary awards, so the switch is greyed off
  // until Monetary reward is on.
  { key: 'spend_categories_enabled', label: 'Spend categories', appliesTo: [PROGRAM], requiresMonetary: true },
];

export const AWARD_FIELDS = [
  // ── Basic details ──────────────────────────────────────────────────────────
  {
    key: 'program_name',
    label: 'Award name',
    section: 'basic',
    type: 'text',
    source: 'column',
    appliesTo: ALL,
    required: true,
  },
  {
    key: 'enabled_divisions',
    label: 'Program scope',
    section: 'basic',
    type: 'division-multiselect',
    source: 'column',
    appliesTo: ALL,
  },
  {
    key: 'company_values',
    label: 'Company values',
    section: 'basic',
    type: 'modal',
    modal: 'company-values',
    source: 'properties',
    appliesTo: PROGRAM_OPEN,
    toggle: 'company_values_enabled',
    conditional: row => Boolean(row.company_values_enabled),
  },
  {
    key: 'milestone_event_type',
    label: 'Milestone type',
    section: 'basic',
    type: 'select',
    source: 'properties',
    options: RECOGNITION_MILESTONE_EVENT_OPTIONS,
    appliesTo: [MILESTONE],
  },
  {
    key: 'recipient_message',
    label: 'Message',
    section: 'basic',
    type: 'text',
    source: 'properties',
    appliesTo: [MILESTONE],
  },
  {
    key: 'short_description',
    label: 'Short description',
    section: 'basic',
    type: 'text',
    source: 'properties',
    appliesTo: PROGRAM_OPEN,
  },

  // ── Monetary ───────────────────────────────────────────────────────────────
  {
    key: 'amount_mode',
    label: 'Amount type',
    section: 'monetary',
    type: 'select',
    source: 'virtual',
    options: AMOUNT_MODE_OPTIONS,
    appliesTo: [PROGRAM],
    toggle: 'monetary_enabled',
    conditional: monetaryOn,
  },
  {
    key: 'award_value_minor',
    label: 'Reward amounts',
    section: 'monetary',
    type: 'modal',
    modal: 'amounts',
    source: 'properties',
    appliesTo: [PROGRAM],
    toggle: 'monetary_enabled',
    conditional: monetaryFixed,
    required: true,
  },
  {
    key: 'max_award_value_minor',
    label: 'Max amount',
    section: 'monetary',
    type: 'currency',
    source: 'properties',
    appliesTo: [PROGRAM],
    toggle: 'monetary_enabled',
    conditional: monetaryFree,
    required: true,
  },
  {
    key: 'award_value_minor',
    label: 'Reward amount',
    section: 'monetary',
    type: 'currency',
    source: 'properties',
    appliesTo: [MILESTONE],
    toggle: 'monetary_enabled',
    conditional: monetaryOn,
    required: true,
  },
  {
    // Combined amount + cadence cell ("£ 100.00 Per <period>"); edits both
    // budget_minor and budget_cadence in one column.
    key: 'budget_minor',
    label: 'Reward budget',
    section: 'monetary',
    type: 'budget',
    source: 'column',
    appliesTo: [PROGRAM],
    toggle: 'monetary_enabled',
    conditional: monetaryOn,
  },
  {
    key: 'categories',
    label: 'Spend categories',
    section: 'monetary',
    type: 'modal',
    modal: 'categories',
    source: 'properties',
    appliesTo: [PROGRAM],
    toggle: 'spend_categories_enabled',
    conditional: row => monetaryOn(row) && Boolean(row.spend_categories_enabled),
  },
  {
    key: 'category_required',
    label: 'Category required',
    section: 'monetary',
    type: 'toggle',
    source: 'properties',
    appliesTo: [PROGRAM],
    toggle: 'spend_categories_enabled',
    conditional: row => monetaryOn(row) && Boolean(row.spend_categories_enabled) && (row.categories || []).length > 0,
  },

  // ── Redemption ───────────────────────────────────────────────────────────────
  {
    key: 'redemption_methods',
    label: 'How to redeem',
    section: 'redemption',
    type: 'multiselect',
    source: 'properties',
    options: RECOGNITION_REDEMPTION_METHOD_CHOICES,
    appliesTo: WITH_MONETARY,
    toggle: 'monetary_enabled',
    conditional: monetaryOn,
    required: true,
  },
  {
    key: 'expiration_months',
    label: 'Expiration',
    section: 'redemption',
    type: 'duration-months',
    source: 'virtual',
    appliesTo: WITH_MONETARY,
    toggle: 'monetary_enabled',
    conditional: monetaryOn,
    required: true,
  },

  // ── Nominations & Approvals ──────────────────────────────────────────────────
  {
    key: 'nominators',
    label: 'Nominators',
    section: 'nominations',
    type: 'employee-multiselect',
    source: 'properties',
    appliesTo: [PROGRAM],
  },
  {
    key: 'approvers',
    label: 'Approvers',
    section: 'nominations',
    type: 'employee-multiselect',
    source: 'properties',
    appliesTo: [PROGRAM],
    required: true,
  },

  // ── Appearance ───────────────────────────────────────────────────────────────
  // Banner/badge imagery — uploadable or pickable from the gallery in the editor.
  {
    key: 'banner_s3_key',
    label: 'Banner',
    section: 'appearance',
    type: 'image',
    source: 'column',
    appliesTo: WITH_IMAGES,
  },
  {
    key: 'badge_s3_key',
    label: 'Badge',
    section: 'appearance',
    type: 'image',
    source: 'column',
    appliesTo: WITH_IMAGES,
    toggle: 'show_badge',
    // Open awards always show a badge (no "Enable badges" toggle); program/milestone
    // gate it behind show_badge.
    conditional: row => (row.program_type === OPEN ? true : Boolean(row.show_badge)),
  },
];

export function featureTogglesForType(programType) {
  return AWARD_FEATURE_TOGGLES.filter(toggle => toggle.appliesTo.includes(programType));
}

export function fieldsForType(programType) {
  return AWARD_FIELDS.filter(field => field.appliesTo.includes(programType));
}
